import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Quote, Star, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fisher-Yates shuffle algorithm
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

// Fallback testimonials in case Google API fails
const fallbackTestimonials = [
  {
    author_name: "Telisa Horne",
    rating: 5,
    text: "Working with Capital Billing has been great for our Hospice Company. Capital Billing Company is very knowledgeable regarding Medicaid updates and completing Medicaid application. Staff is always available to answer questions.",
    relative_time_description: "Client"
  },
  {
    author_name: "Thomas Biddle",
    rating: 5,
    text: "James and his team work their hardest to make sure your practice has a seamless experience with your billing and practice management. His team is a trusted advisor in everything related to billing and EHR software.",
    relative_time_description: "Client"
  },
  {
    author_name: "Dr. Eric Pannell",
    rating: 5,
    text: "Capital Billing Services has been professional and reliable in providing billing and collection services for my practice. They provide the personalized attention of a small billing company with the support of a larger one.",
    relative_time_description: "Client"
  }
];

export const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [businessInfo, setBusinessInfo] = useState({ overall_rating: 0, total_reviews: 0 });
  const [isLoading, setIsLoading] = useState(true);

  // Fetch Google Reviews
  useEffect(() => {
    const fetchReviews = async () => {
      try {
        // Add cache-busting parameter to force fresh fetch
        const cacheBuster = new Date().getTime();
        const response = await fetch(`${API_URL}/api/reviews?_=${cacheBuster}`, {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache',
            'Pragma': 'no-cache'
          }
        });
        if (response.ok) {
          const data = await response.json();
          if (data.reviews && data.reviews.length > 0) {
            setReviews(shuffleArray(data.reviews));
            setBusinessInfo({
              overall_rating: data.overall_rating,
              total_reviews: data.total_reviews
            });
          } else {
            setReviews(shuffleArray(fallbackTestimonials));
          }
        } else {
          setReviews(shuffleArray(fallbackTestimonials));
        }
      } catch (error) {
        console.error('Error fetching reviews:', error);
        setReviews(shuffleArray(fallbackTestimonials));
      } finally {
        setIsLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying || reviews.length === 0) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [isAutoPlaying, reviews.length]);

  const nextTestimonial = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % reviews.length);
  };

  const prevTestimonial = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
  };

  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white" aria-labelledby="testimonials-heading">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-6">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="inline-block bg-teal-500 text-white px-5 py-2 rounded-full text-sm font-semibold">
                Google Reviews
              </span>
              {businessInfo.overall_rating > 0 && (
                <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-full shadow-sm border border-gray-100">
                  <div className="flex gap-0.5" role="img" aria-label={`Rated ${businessInfo.overall_rating} out of 5 stars`}>
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${
                          i < Math.round(businessInfo.overall_rating)
                            ? 'fill-yellow-400 text-yellow-400'
                            : 'fill-gray-200 text-gray-200'
                        }`}
                        aria-hidden="true"
                      />
                    ))}
                  </div>
                  <span className="font-bold text-gray-900">{businessInfo.overall_rating}</span>
                  <span className="text-gray-500 text-sm">({businessInfo.total_reviews} reviews)</span>
                </div>
              )}
            </div>
            <h2 id="testimonials-heading" className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900">
              What Our Clients Are Saying
            </h2>
          </div>
          
          {/* Navigation Arrows */}
          <div className="flex items-center gap-3" role="group" aria-label="Testimonial navigation">
            <button
              onClick={prevTestimonial}
              className="w-12 h-12 rounded-full bg-white border-2 border-gray-200 hover:border-teal-500 hover:bg-teal-50 text-gray-600 hover:text-teal-600 flex items-center justify-center transition-all duration-300 shadow-sm"
              aria-label="Previous testimonial"
              data-testid="prev-testimonial-btn"
            >
              <ChevronLeft className="w-5 h-5" aria-hidden="true" />
            </button>
            <button
              onClick={nextTestimonial}
              className="w-12 h-12 rounded-full bg-navy-600 hover:bg-navy-700 text-white flex items-center justify-center transition-colors shadow-lg"
              style={{ backgroundColor: '#0d4f6e' }}
              aria-label="Next testimonial"
              data-testid="next-testimonial-btn"
            >
              <ChevronRight className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Testimonials Carousel */}
        {isLoading ? (
          <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 mx-auto max-w-4xl animate-pulse min-h-[420px] md:min-h-[400px]" data-testid="testimonials-loading">
            <div className="h-6 bg-gray-200 rounded w-full mb-4"></div>
            <div className="h-6 bg-gray-200 rounded w-3/4"></div>
          </div>
        ) : (
        <div className="relative overflow-hidden rounded-3xl min-h-[420px] md:min-h-[400px]" role="region" aria-label="Client testimonials" aria-live="polite">
          <div 
            className="flex transition-transform duration-500 ease-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {reviews.map((review, index) => (
              <div key={index} className="w-full flex-shrink-0" role="group" aria-roledescription="slide" aria-label={`Testimonial ${index + 1} of ${reviews.length} by ${review.author_name}`}>
                <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 mx-auto max-w-4xl">
                  {/* Stars */}
                  <div className="flex gap-1 mb-6" role="img" aria-label={`${review.rating} out of 5 stars`}>
                    {[...Array(review.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" aria-hidden="true" />
                    ))}
                  </div>

                  {/* Quote Icon */}
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center mb-6 shadow-lg" aria-hidden="true">
                    <Quote className="w-7 h-7 text-white" />
                  </div>

                  {/* Content */}
                  <blockquote className="text-xl md:text-2xl text-gray-700 leading-relaxed mb-8 font-medium line-clamp-6">
                    &ldquo;{review.text}&rdquo;
                  </blockquote>

                  {/* Author */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {review.profile_photo_url ? (
                        <img
                          src={review.profile_photo_url}
                          alt={review.author_name}
                          className="w-14 h-14 rounded-full object-cover shadow-lg"
                        />
                      ) : (
                        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-teal-400 to-cyan-600 flex items-center justify-center text-white font-bold text-xl shadow-lg">
                          {review.author_name.charAt(0)}
                        </div>
                      )}
                      <div>
                        <div className="font-bold text-gray-900 text-lg">{review.author_name}</div>
                        <div className="text-gray-500 text-sm">{review.relative_time_description}</div>
                      </div>
                    </div>
                    
                    {/* Google Attribution */}
                    <a
                      href="https://www.google.com/maps/place/?q=place_id:ChIJ8W2dkfMF9YgR8FK7hgdrCUY"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 text-gray-400 hover:text-gray-600 transition-colors"
                      aria-label="View this review on Google Maps (opens in new tab)"
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                      </svg>
                      <span className="text-xs font-medium hidden sm:inline">View on Google</span>
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        )}

        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-8" role="tablist" aria-label="Select a testimonial">
          {reviews.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setIsAutoPlaying(false);
                setCurrentIndex(index);
              }}
              className={`h-2 rounded-full transition-all duration-300 ${
                index === currentIndex
                  ? 'bg-teal-500 w-8'
                  : 'bg-gray-300 w-2 hover:bg-gray-400'
              }`}
              role="tab"
              aria-selected={index === currentIndex}
              aria-label={`Show testimonial ${index + 1}`}
              data-testid={`testimonial-dot-${index}`}
            />
          ))}
        </div>

        {/* View All Reviews Link */}
        {businessInfo.total_reviews > 0 && (
          <div className="text-center mt-8">
            <a
              href="https://www.google.com/maps/place/?q=place_id:ChIJ8W2dkfMF9YgR8FK7hgdrCUY"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-semibold transition-colors"
              data-testid="view-all-reviews-link"
            >
              View all {businessInfo.total_reviews} reviews on Google
              <ExternalLink className="w-4 h-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
};
